import { Capacitor } from "@capacitor/core";

const STATUS_BAR_COLOR = "#1a1c19";

const ADMOB_TEST_UNIT_IDS = {
  android: {
    rewarded: "ca-app-pub-3940256099942544/5224354917",
  },
  ios: {
    rewarded: "ca-app-pub-3940256099942544/1712485313",
  },
};

const ADMOB_UNIT_IDS = {
  android: {
    rewarded:
      process.env.REACT_APP_ADMOB_ANDROID_REWARDED_ID ||
      "ca-app-pub-1782274844122881/1549906801",
  },
  ios: {
    rewarded:
      process.env.REACT_APP_ADMOB_IOS_REWARDED_ID ||
      "ca-app-pub-1782274844122881/3504142997",
  },
};

const ADMOB_REWARD_SETTLE_MS = 400;

const ignore = () => {};

let initialized = false;
let adMobInitialized = null;

export function isNativeApp() {
  try {
    return Capacitor.isNativePlatform();
  } catch (error) {
    return false;
  }
}

export function getNativePlatform() {
  try {
    return Capacitor.getPlatform();
  } catch (error) {
    return "web";
  }
}

export function canShowAds() {
  return isNativeApp() && getNativePlatform() === "android";
}

function openExternalLinksInSystemBrowser(Browser) {
  const handler = (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    const anchor =
      event.target && event.target.closest
        ? event.target.closest("a[href]")
        : null;
    if (!anchor) return;
    const href = anchor.getAttribute("href");
    if (!href || href.startsWith("#")) return;

    let url = null;
    try {
      url = new URL(href, window.location.href);
    } catch (error) {
      return;
    }
    if (url.protocol !== "http:" && url.protocol !== "https:") return;
    if (url.host === window.location.host) return;

    event.preventDefault();
    Browser.open({ url: url.href }).catch(ignore);
  };

  document.addEventListener("click", handler, true);
}

export async function initNativeApp() {
  if (initialized || !isNativeApp()) return;
  initialized = true;

  const platform = getNativePlatform();
  document.documentElement.setAttribute("data-native-platform", platform);

  const [appModule, splashModule, statusBarModule, browserModule] =
    await Promise.all([
      import("@capacitor/app"),
      import("@capacitor/splash-screen"),
      import("@capacitor/status-bar"),
      import("@capacitor/browser"),
    ]);

  const { App } = appModule;
  const { SplashScreen } = splashModule;
  const { StatusBar, Style } = statusBarModule;
  const { Browser } = browserModule;

  await StatusBar.setStyle({ style: Style.Dark }).catch(ignore);
  if (platform === "android") {
    await StatusBar.setBackgroundColor({ color: STATUS_BAR_COLOR }).catch(
      ignore,
    );
  }

  await SplashScreen.hide({ fadeOutDuration: 250 }).catch(ignore);

  App.addListener("backButton", ({ canGoBack }) => {
    if (canGoBack && window.history.length > 1) {
      window.history.back();
      return;
    }
    App.minimizeApp().catch(ignore);
  }).catch(ignore);

  openExternalLinksInSystemBrowser(Browser);
}

function resolveAdMobUnit(placement, options = {}) {
  const platform = getNativePlatform();
  const admobid = { ...(ADMOB_UNIT_IDS[platform] || {}) };
  const dev =
    Boolean(options.dev) ||
    process.env.NODE_ENV !== "production" ||
    !admobid[placement];
  if (dev) {
    admobid.rewarded = ADMOB_TEST_UNIT_IDS[platform]?.rewarded;
  }
  return { adId: admobid[placement], dev };
}

async function gatherAdMobConsent(AdMob, AdmobConsentStatus) {
  try {
    let consent = await AdMob.requestConsentInfo();
    if (
      consent.status === AdmobConsentStatus.REQUIRED &&
      consent.isConsentFormAvailable
    ) {
      consent = await AdMob.showConsentForm();
    }
    if (consent.canRequestAds === false) return false;
  } catch (error) {
    ignore(error);
  }

  if (getNativePlatform() === "ios") {
    const { status } = await AdMob.trackingAuthorizationStatus().catch(() => ({
      status: "",
    }));
    if (status === "notDetermined") {
      await AdMob.requestTrackingAuthorization().catch(ignore);
    }
  }
  return true;
}

async function prepareAdMob(dev) {
  const adMobModule = await import("@capacitor-community/admob");
  const { AdMob, AdmobConsentStatus } = adMobModule;
  const canRequestAds = await gatherAdMobConsent(AdMob, AdmobConsentStatus);
  if (!canRequestAds) return null;
  if (!adMobInitialized) {
    adMobInitialized = AdMob.initialize({ initializeForTesting: dev }).catch(
      (error) => {
        adMobInitialized = null;
        throw error;
      },
    );
  }
  await adMobInitialized;
  return adMobModule;
}

async function presentFullScreenAd(AdMob, events, show) {
  let earned = !events.reward;
  let settle = ignore;
  const outcome = new Promise((resolve) => {
    settle = resolve;
  });
  const handles = await Promise.all(
    [
      events.reward &&
        AdMob.addListener(events.reward, () => {
          earned = true;
        }),
      AdMob.addListener(events.dismissed, () => {
        window.setTimeout(() => settle(earned), ADMOB_REWARD_SETTLE_MS);
      }),
      AdMob.addListener(events.failedToShow, () => settle(false)),
    ].filter(Boolean),
  );

  show()
    .then(() => {
      if (events.reward) earned = true;
    })
    .catch(() => {
      if (!(events.reward && earned)) settle(false);
    });

  const result = await outcome;
  handles.forEach((handle) => Promise.resolve(handle.remove()).catch(ignore));
  return result;
}

export async function showRewardedAd(options = {}) {
  if (!canShowAds()) return false;
  const { adId, dev } = resolveAdMobUnit("rewarded", options);
  const adMob = await prepareAdMob(dev);
  if (!adMob) return false;
  const { AdMob, RewardAdPluginEvents } = adMob;
  await AdMob.prepareRewardVideoAd({ adId, isTesting: dev });
  return presentFullScreenAd(
    AdMob,
    {
      reward: RewardAdPluginEvents.Rewarded,
      dismissed: RewardAdPluginEvents.Dismissed,
      failedToShow: RewardAdPluginEvents.FailedToShow,
    },
    () => AdMob.showRewardVideoAd(),
  );
}


